import { api } from "@/lib/api";
import { AppUser } from "../schema";

export interface AppUserDetail extends AppUser {
  totalSessionsCompleted: number;
  completedJourneys: number;
  qualifiedDays: number;
  moodDataAvailable: boolean;
  cycleTrackingConnected: boolean;
  sleepDataConnected: boolean;
  activityDataConnected: boolean;
  nutritionDataPresent: boolean;
  weightDataPresent: boolean;
  lastSyncTimestamp: string | null;
}

export interface UsersFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  country?: string;
  ageMin?: number;
  ageMax?: number;
  religion?: string;
  relationshipStatus?: string;
  hasChildren?: string;
  plan?: string;
  sortBy?: string;
}

export interface UsersResponse {
  users: AppUser[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UpdateUserStatusRequest {
  action: "suspend" | "restore" | "delete";
  reason?: string;
}

export interface UpdateUserStatusResponse {
  message: string;
}

export class UsersService {
  private static readonly BASE_URL = "/admin/users";

  static async getUsers(filters?: UsersFilters): Promise<UsersResponse> {
    const params = new URLSearchParams();

    if (filters?.page) {
      params.append("page", filters.page.toString());
    }
    if (filters?.limit) {
      params.append("limit", filters.limit.toString());
    }
    if (filters?.search && filters.search.trim()) {
      params.append("search", filters.search.trim());
    }
    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }

    // Demographic filters
    if (filters?.country && filters.country !== "all") {
      params.append("country", filters.country);
    }
    if (filters?.ageMin !== undefined) {
      params.append("ageMin", filters.ageMin.toString());
    }
    if (filters?.ageMax !== undefined) {
      params.append("ageMax", filters.ageMax.toString());
    }
    if (filters?.religion && filters.religion !== "all") {
      params.append("religion", filters.religion);
    }
    if (filters?.relationshipStatus && filters.relationshipStatus !== "all") {
      params.append("relationshipStatus", filters.relationshipStatus);
    }
    if (filters?.hasChildren && filters.hasChildren !== "all") {
      params.append("hasChildren", filters.hasChildren);
    }
    if (filters?.plan && filters.plan !== "all") {
      params.append("plan", filters.plan);
    }
    if (filters?.sortBy) {
      params.append("sortBy", filters.sortBy);
    }

    const queryString = params.toString();
    const url = queryString ? `${this.BASE_URL}?${queryString}` : this.BASE_URL;

    return api.get<UsersResponse>(url);
  }

  static async getUserById(userId: string): Promise<AppUserDetail> {
    return api.get<AppUserDetail>(`${this.BASE_URL}/${userId}`);
  }
}
